// Auto-generated by splitter.py — do not regenerate; edit directly.

import React from 'react';

export const CHIP_TEXT_TONES = {
  neutral: 'text-[#6B7280]',
  green:   'text-[#1F6A3A]',
  amber:   'text-[#8A5A12]',
  red:     'text-[#9B1F1F]',
  blue:    'text-[#1E40AF]',
  violet:  'text-[#5B3A8C]',
  dark:    'text-[#1F2937]',
};
const CHIP_DOT_COLORS = {
  green:   'bg-[#2F8B4F]',
  amber:   'bg-[#C48319]',
  red:     'bg-[#C53030]',
  blue:    'bg-[#2563EB]',
  violet:  'bg-[#6D44A8]',
  neutral: 'bg-[#9CA3AF]',
  dark:    'bg-[#1F2937]',
};

export const Chip = ({ tone = 'neutral', children, dot = false, size = 'md' }) => {
  // Enterprise table chips: muted text + optional dot indicator, no pill backgrounds.
  // For status indicators in tables, pass dot={true} for a dot prefix.
  const textTones = CHIP_TEXT_TONES;
  const dotColors = CHIP_DOT_COLORS;
  const sz = size === 'sm' ? 'text-[11px]' : 'text-[12px]';
  return (
    <span className={`inline-flex items-center gap-1.5 font-medium whitespace-nowrap ${sz} ${textTones[tone]}`}>
      {dot && <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColors[tone]}`} />}
      {children}
    </span>
  );
};


const STATUS_CHIP_MAP = {
    'Live': { tone: 'green', dot: true },
    'Upcoming': { tone: 'blue' },
    'Draft': { tone: 'neutral' },
    'Completed': { tone: 'neutral' },
    'Cancelled': { tone: 'red' },
    'Active': { tone: 'green', dot: true },
    'Suspended': { tone: 'red' },
    'Pending': { tone: 'amber' },
    'Pending approval': { tone: 'amber' },
    'Approved': { tone: 'green' },
    'Rejected': { tone: 'red' },
    'Blocked': { tone: 'red' },
    'Needs info': { tone: 'amber' },
    'Under review': { tone: 'blue' },
    'Paid': { tone: 'green' },
    'Failed': { tone: 'red' },
    'Refunded': { tone: 'violet' },
    'Held': { tone: 'amber' },
    'Healthy': { tone: 'green', dot: true },
    'Degraded': { tone: 'amber', dot: true },
    'Online': { tone: 'green', dot: true },
    'Ready': { tone: 'blue' },
    'Setup': { tone: 'amber' },
    'Closed': { tone: 'neutral' },
    'Issued': { tone: 'green' },
    'Investigating': { tone: 'blue' },
    'Open': { tone: 'amber' },
    'Resolved': { tone: 'neutral' },
    'Verified': { tone: 'green' },
    'Pending verification': { tone: 'amber' },
    'Low': { tone: 'neutral' },
    'Medium': { tone: 'amber' },
    'High': { tone: 'red' },
    'Success': { tone: 'green' },
    'Settled': { tone: 'green' },
    'Reversed': { tone: 'violet' },
    'Matched': { tone: 'green' },
    'Unmatched': { tone: 'amber' },
    'Awaiting docs': { tone: 'amber' },
    'Auto-blocked': { tone: 'red' },
    'Monitoring': { tone: 'blue' },
    'On hold': { tone: 'amber' },
    'Closed — benign': { tone: 'neutral' },
    'Voided': { tone: 'neutral' },
    'Delivered': { tone: 'green' },
    'OK': { tone: 'green' },
    'Denied': { tone: 'amber' },
    'Alert': { tone: 'amber' },
    'Flagged': { tone: 'red' },
    'Sent': { tone: 'blue' },
    'Inactive': { tone: 'neutral' },
    'Archived': { tone: 'neutral' },
    // Call Center (real WhatsApp Business Calling)
    'Ringing': { tone: 'amber' },
    'Answered': { tone: 'green' },
    'Missed': { tone: 'red' },
    'Declined': { tone: 'neutral' },
};

export const StatusChip = ({ status }) => {
  const map = STATUS_CHIP_MAP;
  const cfg = map[status] || { tone: 'neutral' };
  // StatusChip always shows a dot — this is the operational status indicator pattern.
  return <Chip tone={cfg.tone} dot={true}>{status}</Chip>;
};

// Spinner — refined arc loader. A thin track with a rotating quarter-arc.
// Sizes: sm (12px), md (15px default), lg (22px). Inline-block so it sits cleanly in buttons.
// Tones: 'current' (inherits text color), 'white' (for dark buttons), 'muted' (for placeholders).

const STATUS_DOT_MAP = {
    'Live':              { color: '#1F6A3A', pulse: true },
    'Active':            { color: '#1F6A3A', pulse: true },
    'Online':            { color: '#1F6A3A', pulse: true },
    'Healthy':           { color: '#1F6A3A' },
    // The other things payment health can honestly be. Without these the derived states fell to
    // the map's grey default, so "Failing" would have been drawn in the same colour as "Closed"
    // — a red condition rendered as a neutral one. Amber → caution, red → broken (the house
    // severity ladder: green → amber → rose).
    'Degraded':          { color: '#B7791F' },
    'Failing':           { color: '#9B1F1F', pulse: true },
    'Syncing':           { color: '#1E40AF', pulse: true },
    'Disabled':          { color: '#9CA3AF' },
    'Never synced':      { color: '#9CA3AF' },
    'Idle':              { color: '#9CA3AF' },
    'Upcoming':          { color: '#1E40AF' },
    'Under review':      { color: '#1E40AF' },
    'Investigating':     { color: '#1E40AF' },
    'Monitoring':        { color: '#1E40AF' },
    'Sent':              { color: '#1E40AF' },
    'Ready':             { color: '#1E40AF' },
    'Draft':             { color: '#9CA3AF' },
    // PROMO / DISCOUNT LIFECYCLE. Without these the four states normalizePromotionRule now
    // derives all fell to the grey default, so "Expired" and "Scheduled" would have been drawn
    // identically — which is the same failure as the old status that could only ever say Active.
    // Blue = ahead of us, amber = needs a decision, grey = over.
    'Scheduled':         { color: '#1E40AF' },
    'Paused':            { color: '#B7791F' },
    'Exhausted':         { color: '#B7791F' },
    'Expired':           { color: '#9CA3AF' },
    'Completed':         { color: '#6B7280' },
    'Closed':            { color: '#6B7280' },
    'Resolved':          { color: '#6B7280' },
    'Inactive':          { color: '#9CA3AF' },
    'Archived':          { color: '#9CA3AF' },
    'Voided':            { color: '#9CA3AF' },
    'Closed — benign':   { color: '#9CA3AF' },
    'Cancelled':         { color: '#9B1F1F' },
    'Rejected':          { color: '#9B1F1F' },
    'Blocked':           { color: '#9B1F1F' },
    'Failed':            { color: '#9B1F1F' },
    'Suspended':         { color: '#9B1F1F' },
    'Auto-blocked':      { color: '#9B1F1F' },
    'High':              { color: '#9B1F1F' },
    'Flagged':           { color: '#9B1F1F' },
    'Pending':           { color: '#C48319' },
    'Pending approval':  { color: '#C48319' },
    'Needs info':        { color: '#C48319' },
    'Held':              { color: '#C48319' },
    'Partial':           { color: '#C48319' },
    'On hold':           { color: '#C48319' },
    'Setup':             { color: '#C48319' },
    'Open':              { color: '#C48319' },
    'Unmatched':         { color: '#C48319' },
    'Awaiting docs':     { color: '#C48319' },
    'Pending verification':{ color: '#C48319' },
    'Degraded':          { color: '#C48319' },
    'Medium':            { color: '#C48319' },
    'Denied':            { color: '#C48319' },
    'Alert':             { color: '#C48319' },
    'Approved':          { color: '#1F6A3A' },
    'Paid':              { color: '#1F6A3A' },
    'Success':           { color: '#1F6A3A' },
    'Settled':           { color: '#1F6A3A' },
    'Verified':          { color: '#1F6A3A' },
    'Matched':           { color: '#1F6A3A' },
    'Delivered':         { color: '#1F6A3A' },
    'Issued':            { color: '#1F6A3A' },
    'OK':                { color: '#1F6A3A' },
    'Low':               { color: '#6B7280' },
    'Refunded':          { color: '#5B3A8C' },
    'Partly refunded':   { color: '#5B3A8C' },
    'Reversed':          { color: '#5B3A8C' },
    // Call Center (real WhatsApp Business Calling)
    'Ringing':           { color: '#C48319', pulse: true },
    'Answered':          { color: '#1F6A3A' },
    'Missed':            { color: '#9B1F1F' },
    'Declined':          { color: '#6B7280' },
};

// A payment's (and an order's) status as STORED — successful/pending/failed/expired/cancelled — is
// shown as its word here, in one place, so a page passes the record's own value straight through.
const STORED_STATUS_WORD = { successful: 'Success', pending: 'Pending', failed: 'Failed', expired: 'Expired', cancelled: 'Cancelled' };

export const StatusDot = ({ status: given }) => {
  const status = STORED_STATUS_WORD[given] || given;
  const map = STATUS_DOT_MAP;
  const cfg = map[status] || { color: '#6B7280' };
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] text-[#1F2937] whitespace-nowrap">
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.pulse ? 'animate-pulse' : ''}`} style={{ backgroundColor: cfg.color }} />
      {status}
    </span>
  );
};
